<script lang="ts">
import Modal from '../Modal/Modal.vue';
import { mergeSwitchWtParams } from '../../utils/tracking';
import { prepareContactFormUrl } from '../../utils/form';
import { ref } from 'vue';
import ContactBox from '../ContactBox/ContactBox.vue';
import Play from '../Play/Play.vue';
import Icon from '../Icon/Icon.vue';
import type { WindowWika } from '../../types/window';

type ListTeaserProps = {
  address?: string;
  company?: string;
  country_name?: string;
  country?: string;
  email?: string;
  fax?: string;
  files?: Array<{ url: string; name: string }>;
  filterCategories?: Record<string, string[] | string>;
  flag?: string; // @deprecated
  fromEmail?: string;
  image?: string;
  isVideo?: boolean;
  link?: string;
  phone?: string;
  subHeading?: string;
  target?: string;
  text?: string;
  title?: string;
  website_text?: string;
  website?: string;
  wtParams?: object;
};

type Props = {
  result: ListTeaserProps;
  isAddressTeaser?: boolean;
  wtParams: Record<string, string>;
  useForm: boolean;
  type: 'news' | 'media' | 'event';
};

const _window = window as unknown as WindowWika;
</script>

<script setup lang="ts">
const { result } = defineProps<Props>();

const isPdf = ref(false);
const isFocused = ref(false);
const isHovered = ref(false);
const isModalOpen = ref(false);

const { wtTrack } = _window;

const mergeWtParams = (globalWtParams: object, itemWtParams: object, itemParams?: object) =>
  mergeSwitchWtParams(globalWtParams, itemWtParams, itemParams);

const linkIsPDF = (link: string) => Boolean(link && link.split('?')[0].match(/\.pdf$/g));

const contactFormUrl = prepareContactFormUrl(result);
</script>

<template>
  <ContactBox
    v-if="isAddressTeaser"
    :address="result.address"
    :contact-form-url="contactFormUrl"
    :country="result.country"
    :email="result.email"
    :fax="result.fax"
    :from-email="result.fromEmail"
    :head="result.company"
    :phone="result.phone"
    :use-form="useForm"
    :website="result.website"
    :website-text="result.website_text"
  />
  <div
    v-else
    :class="[
      $style.container,
      type === 'media' && $style.mediaContainer,
      (isFocused || isHovered) && $style.hover,
    ]"
  >
    <a
      v-if="result.link && result.isVideo"
      :class="$style.teaserLink"
      @blur="isFocused = false"
      @click="
        ((isModalOpen = true),
        result.wtParams &&
          wtTrack?.(
            { cat: wtParams.doctype },
            mergeWtParams(
              wtParams,
              {
                ...result.wtParams,
                filename: result.link,
              },
              {},
            ),
          ))
      "
      @focus="isFocused = true"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    />
    <a
      v-else-if="result.link"
      aria-label="goto detail"
      :class="$style.teaserLink"
      :href="result.link"
      :set="isPdf = linkIsPDF(result.link)"
      :target="isPdf ? '_blank' : result.target ? result.target : '_self'"
      @blur="isFocused = false"
      @click="
        wtParams &&
        wtTrack?.(
          {
            cat: wtParams.doctype,
          },
          mergeWtParams(wtParams, {
            ...result.wtParams,
            filename: result.link,
            ...(isPdf
              ? {
                  type: 'PDF',
                }
              : {}),
          }),
        )
      "
      @focus="isFocused = true"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    />

    <div v-if="result.image" :class="[$style.mediaLink, result.isVideo && $style.videoLink]">
      <Play v-if="result.isVideo" :is-focused="isFocused" :is-hovered="isHovered" />
      <img
        :alt="result.text?.replace(/(<([^>]+)>)/gi, '')"
        :class="$style.image"
        :src="result.image"
      />
    </div>
    <div :class="$style.caption">
      <div v-if="result.subHeading" :class="$style.headingRow">
        <div :class="$style.subHeading">
          <Icon
            v-if="['news', 'media', 'event'].includes(type)"
            name="calendar_today"
            size="small"
          />
          {{ result.subHeading }}
        </div>
      </div>
      <h5 v-if="result.title" :class="$style.title">
        {{ result.title }}
      </h5>
      <div v-if="result.country_name" :class="$style.country">
        <Icon name="location_on" size="small" />
        {{ result.country_name }}
      </div>
      <div
        v-if="result.company && !result.phone && !result.fax && !result.website && !result.email"
        :class="$style.text"
      >
        {{ result.company }}
      </div>
      <div
        v-if="result.company && (result.phone || result.fax || result.website || result.email)"
        :class="$style.headingRow"
      >
        <h5 :class="$style.title">
          {{ result.company }}
        </h5>
      </div>
      <div v-if="result.text" :class="$style.title" v-html="result.text" />
    </div>

    <Modal
      v-if="result.isVideo"
      :is-modal-open="isModalOpen"
      :is-video="result.isVideo"
      @close-modal="isModalOpen = false"
    >
      <iframe
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        :data-src="result.link"
        frameborder="0"
      />
    </Modal>
  </div>
</template>

<style module lang="scss">
@forward './ListTeaser.scss';
</style>
